import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import s from './Refollow.module.scss';

const WA_ROWS = [
  ['wa_sent', 'Sent'],
  ['wa_delivered', 'Delivered'],
  ['wa_read', 'Read'],
  ['wa_replied', 'Replied'],
  ['wa_failed', 'Failed'],
];

export default function RefollowQueueCard({ stats, loading, onClick }) {
  const due = Number(stats?.due_today) || 0;
  const done = Number(stats?.done_today) || 0;
  const cancelled = Number(stats?.cancelled_today) || 0;
  const waSent = Number(stats?.wa_sent) || 0;
  const [waOpen, setWaOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const waBtnRef = useRef(null);
  const waWrapRef = useRef(null);

  const placeMenu = () => {
    const el = waBtnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuW = 168;
    const gap = 6;
    let left = r.right - menuW;
    left = Math.max(8, Math.min(left, window.innerWidth - menuW - 8));
    setMenuStyle({
      position: 'fixed',
      left,
      top: r.top - gap,
      transform: 'translateY(-100%)',
      zIndex: 10050,
    });
  };

  useLayoutEffect(() => {
    if (!waOpen) {
      setMenuStyle(null);
      return undefined;
    }
    placeMenu();
    const onReflow = () => placeMenu();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [waOpen, loading]);

  useEffect(() => {
    if (!waOpen) return undefined;
    const onDoc = (event) => {
      const t = event.target;
      if (waWrapRef.current?.contains(t)) return;
      if (t.closest?.(`.${s.refollowQueueWaMenuPortal}`)) return;
      setWaOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [waOpen]);

  const openQueue = (e) => {
    if (e.target.closest(`.${s.refollowQueueWaWrap}`)) return;
    onClick?.(e);
  };

  const waMenu = waOpen && !loading && menuStyle && createPortal(
    <div className={s.refollowQueueWaMenuPortal} style={menuStyle} role="menu">
      {WA_ROWS.map(([key, label]) => (
        <div
          key={key}
          className={`${s.refollowQueueWaMenuRow}${key === 'wa_failed' ? ` ${s.refollowQueueWaMenuFailed}` : ''}`}
        >
          <span>{label}</span>
          <strong>{Number(stats?.[key]) || 0}</strong>
        </div>
      ))}
    </div>,
    document.body,
  );

  return (
    <>
      <div
        className={`${s.refollowQueueCard}${waOpen ? ` ${s.refollowQueueCardWaOpen}` : ''}`}
        role="button"
        tabIndex={0}
        onClick={openQueue}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openQueue(e);
          }
        }}
        aria-label="Follow up queue"
      >
        <div className={s.refollowQueueCardInner}>
          <i className={`fa fa-chevron-right ${s.refollowQueueChev}`} aria-hidden />
          <strong className={s.refollowQueueTitle}>FOLLOW UP</strong>
          <div className={s.refollowQueueStats}>
            <span className={s.refollowQueueStatBox}>
              <span className={s.refollowQueueStatValue}>{loading ? '…' : due}</span>
              <span className={s.refollowQueueStatLabel}>Due today</span>
            </span>
            <span className={s.refollowQueueStatBox}>
              <span className={s.refollowQueueStatValue}>{loading ? '…' : done}</span>
              <span className={s.refollowQueueStatLabel}>Done today</span>
            </span>
            <span className={s.refollowQueueStatBox}>
              <span className={`${s.refollowQueueStatValue} ${s.refollowQueueStatValueMuted}`}>
                {loading ? '…' : cancelled}
              </span>
              <span className={s.refollowQueueStatLabel}>Cancelled</span>
            </span>
            <div className={s.refollowQueueWaWrap} ref={waWrapRef}>
              <button
                ref={waBtnRef}
                type="button"
                className={`${s.refollowQueueStatBox} ${s.refollowQueueWaStatBox}${waOpen ? ` ${s.refollowQueueWaStatBoxOpen}` : ''}`}
                aria-expanded={waOpen}
                aria-haspopup="menu"
                title="Follow-up WhatsApp sends today (IST)"
                onClick={(e) => {
                  e.stopPropagation();
                  setWaOpen((open) => !open);
                }}
              >
                <span className={s.refollowQueueStatValue}>{loading ? '…' : waSent}</span>
                <span className={s.refollowQueueWaStatLabel}>
                  WA sent
                  <ChevronDown size={12} aria-hidden className={s.refollowQueueWaChev} />
                </span>
              </button>
            </div>
          </div>
        </div>
        <div className={s.refollowQueueTrack} aria-hidden>
          <div className={s.refollowQueueTrackFill} style={{ width: due > 0 ? '100%' : '0%' }} />
        </div>
      </div>
      {waMenu}
    </>
  );
}
